<template>
  <section class="dashboard">
    <report-filter-bar
      v-model="filter"
      :preset="preset"
      :busy="isAnyLoading"
      :compare-label="compareLabel"
      @preset="applyPreset"
      @change="refresh"
      @refresh="refresh"
    />

    <!-- هشدارها بالاتر از همه: کاری که همین حالا باید انجام شود -->
    <b-row v-if="alerts.length">
      <b-col cols="12">
        <div class="dashboard__alerts">
          <b-alert
            v-for="alert in alerts"
            :key="alert.Key"
            :variant="alert.Severity"
            show
            class="dashboard__alert mb-1"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                :icon="alertIcon(alert.Severity)"
                size="17"
                class="mr-75 flex-shrink-0"
              />
              <div class="flex-grow-1">
                <strong>{{ alert.Title }}</strong>
                <div class="dashboard__alert-text">
                  {{ alert.Description }}
                </div>
              </div>
              <b-button
                v-if="alert.Route && hasRoute(alert.Route)"
                size="sm"
                :variant="`outline-${alert.Severity}`"
                class="flex-shrink-0"
                @click="$router.push({ name: alert.Route })"
              >
                رسیدگی
              </b-button>
            </div>
          </b-alert>
        </div>
      </b-col>
    </b-row>

    <!-- ردیف KPI -->
    <b-row class="match-height">
      <b-col
        v-for="(item, index) in kpis"
        :key="item.Key"
        xl="3"
        md="4"
        sm="6"
        cols="12"
        class="mb-1"
      >
        <kpi-card
          :kpi="item"
          :skin="skin"
          :color-index="index"
        />
      </b-col>
    </b-row>

    <!--
      درآمد و تعداد در دو نمودار جدا هستند، نه دو محور روی یک نمودار:
      دو مقیاس روی یک پلات همبستگی جعلی می‌سازد.
    -->
    <b-row class="match-height">
      <b-col
        :lg="capabilities.Shop ? 8 : 12"
        cols="12"
        class="mb-1"
      >
        <report-chart
          :series="sales"
          :title="salesTitle"
          :subtitle="rangeLabel"
          :metrics="primaryMetrics"
          type="area"
          :height="320"
          :loading="loading('sales')"
          :skin="skin"
        />
      </b-col>

      <b-col
        v-if="capabilities.Shop"
        lg="4"
        cols="12"
        class="mb-1"
      >
        <report-funnel
          :steps="funnel"
          :loading="loading('sales')"
          :skin="skin"
        />
      </b-col>
    </b-row>

    <b-row
      v-if="secondaryMetrics.length"
      class="match-height"
    >
      <b-col
        cols="12"
        class="mb-1"
      >
        <report-chart
          :series="sales"
          title="تعداد"
          :subtitle="countSubtitle"
          :metrics="secondaryMetrics"
          type="bar"
          :height="260"
          :loading="loading('sales')"
          :skin="skin"
        />
      </b-col>
    </b-row>

    <b-row
      v-if="capabilities.Shop"
      class="match-height"
    >
      <b-col
        lg="7"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="topProducts"
          title="پرفروش‌ترین محصول‌ها"
          :subtitle="rangeLabel"
          label-title="محصول"
          show-image
          :loading="loading('topProducts')"
          :route-for="productRoute"
        />
      </b-col>

      <b-col
        lg="5"
        cols="12"
        class="mb-1"
      >
        <report-table
          :breakdown="channels"
          title="درگاه پرداخت"
          :subtitle="rangeLabel"
          label-title="درگاه"
          :loading="loading('channels')"
        />
      </b-col>
    </b-row>

    <!-- میان‌بر به گزارش‌های تفصیلی -->
    <b-row>
      <b-col cols="12">
        <b-card no-body>
          <b-card-body>
            <h4 class="mb-1">
              گزارش‌های تفصیلی
            </h4>
            <div class="d-flex flex-wrap">
              <b-button
                v-for="link in reportLinks"
                :key="link.route"
                variant="flat-primary"
                size="sm"
                class="mr-50 mb-50"
                @click="$router.push({ name: link.route })"
              >
                <feather-icon
                  :icon="link.icon"
                  size="14"
                />
                <span class="ml-25">{{ link.title }}</span>
              </b-button>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { BAlert, BCardBody } from 'bootstrap-vue'
import reportMixin from '@core/mixins/report'

export default {
  name: 'Dashboard',
  components: { BAlert, BCardBody },
  mixins: [reportMixin],
  data() {
    return {
      kpis: [],
      sales: null,
      funnel: [],
      topProducts: null,
      channels: null,
      alerts: [],
      capabilities: {},
      compareLabel: null,
    }
  },
  computed: {
    /** عنوان نمودار اصلی به نوع سایت بستگی دارد */
    salesTitle() {
      if (this.capabilities.Shop) return 'روند فروش'
      if (this.capabilities.Duty) return 'روند درخواست خدمات'
      if (this.capabilities.Lead) return 'روند سرنخ‌ها'
      return 'روند بازدید محتوا'
    },

    countSubtitle() {
      return `${this.rangeLabel} — سری‌های شمارشی جدا از مبلغ رسم شده‌اند`
    },

    /** متریک‌های پولی برای نمودار اصلی */
    primaryMetrics() {
      const metrics = (this.sales && this.sales.Metrics) || []
      const price = metrics.filter(m => m.Format === 'price').map(m => m.Key)
      // سایت بدون فروش متریک پولی ندارد؛ آن‌وقت همان شمارشی‌ها رسم می‌شوند
      return price.length ? price : metrics.slice(0, 3).map(m => m.Key)
    },

    /** متریک‌های شمارشی که نباید با مبلغ روی یک محور بروند */
    secondaryMetrics() {
      const metrics = (this.sales && this.sales.Metrics) || []
      const hasPrice = metrics.some(m => m.Format === 'price')
      if (!hasPrice) return []

      return metrics
        .filter(m => m.Format === 'number' && m.Key !== 'aov')
        .map(m => m.Key)
    },

    reportLinks() {
      const links = []

      if (this.capabilities.Shop) {
        links.push({ route: 'report-sales', title: 'گزارش فروش', icon: 'TrendingUpIcon' })
        links.push({ route: 'report-products', title: 'گزارش محصولات', icon: 'PackageIcon' })
        links.push({ route: 'report-customers', title: 'گزارش مشتریان', icon: 'UsersIcon' })
        links.push({ route: 'report-abandoned', title: 'سبد رهاشده', icon: 'ShoppingCartIcon' })
        links.push({ route: 'report-coupons', title: 'کدهای تخفیف', icon: 'TagIcon' })
      }

      if (this.capabilities.Duty) {
        links.push({ route: 'report-duty', title: 'گزارش خدمات', icon: 'ClipboardIcon' })
      }

      if (this.capabilities.Lead) {
        links.push({ route: 'report-leads', title: 'گزارش سرنخ‌ها', icon: 'InboxIcon' })
      }

      links.push({ route: 'report-traffic', title: 'گزارش ترافیک', icon: 'ActivityIcon' })

      if (this.capabilities.Blog || this.capabilities.Project) {
        links.push({ route: 'report-content', title: 'گزارش محتوا', icon: 'FileTextIcon' })
      }

      links.push({ route: 'report-operations', title: 'سلامت سیستم', icon: 'HeartIcon' })

      return links
    },
  },
  created() {
    this.refresh()
  },
  methods: {
    refresh() {
      // یک فراخوانی، همه‌ی بلوک‌های داشبورد — تا صفحه‌ی اول سریع بالا بیاید
      this.fetchReport('overview', this.$api.report.overview).then(data => {
        if (!data) return

        this.kpis = data.Kpi || []
        this.sales = data.Sales
        this.funnel = data.Funnel || []
        this.topProducts = data.TopProducts
        this.channels = data.Channels
        this.alerts = data.Alerts || []
        this.capabilities = data.Capabilities || {}
        this.compareLabel = data.CompareRange ? data.CompareRange.Label : null
      })
    },

    /**
     * چون همه‌چیز از یک اندپوینت می‌آید، وضعیت loading همه‌ی بلوک‌ها یکی است.
     */
    loading() {
      return !!this.reportLoading.overview
    },

    alertIcon(severity) {
      if (severity === 'danger') return 'AlertOctagonIcon'
      if (severity === 'warning') return 'AlertTriangleIcon'
      return 'InfoIcon'
    },

    /**
     * جلوگیری از پیمایش به روتی که وجود ندارد.
     * vue-router 3.4 متد getRoutes ندارد، پس با resolve چک می‌کنیم:
     * نام ناشناس به مسیر «/» رزولو می‌شود.
     */
    hasRoute(name) {
      try {
        return this.$router.resolve({ name }).route.name === name
      } catch (e) {
        return false
      }
    },

    productRoute(row) {
      return row.Id ? { name: 'updateproduct', params: { id: row.Id } } : null
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard {
  &__alert {
    padding: 0.7rem 1rem;
  }

  &__alert-text {
    font-size: 0.78rem;
    opacity: 0.85;
  }
}
</style>
